/* ============================================================================
   PANIER — Cloudinary + /cart/add.js  (punto 9, fase D)
   ----------------------------------------------------------------------------
   El ultimo tramo: del « Continuer » del Studio al carrito real de Shopify,
   con el diseno viajando dentro del pedido.

   POR QUE CLOUDINARY Y NO EL PROPIO SHOPIFY: las line item properties de
   Shopify son TEXTO. No admiten archivos. Asi que la foto del cliente tiene
   que vivir en algun sitio y en la property viaja su URL. Cloudinary con un
   preset unsigned deja subir desde el navegador sin backend y sin clave
   secreta — que es toda la arquitectura de este proyecto.

   LA REGLA DE TAMANO QUE MANDA AQUI. `_design_json` va dentro del pedido y
   Shopify corta las properties largas. El spec fija ~4 Ko. Un dataUrl de una
   foto son cientos de kilobytes, asi que el JSON que se manda NO puede llevar
   imagenes: se sustituyen por las URL de Cloudinary. Si esto se hiciera mal,
   el pedido llegaria truncado y el archivo de impresion seria irreconstruible
   — el fallo se descubriria con el pedido ya pagado.

   DONDE VIVEN cloud_name Y PRESET: no en el repositorio. En produccion los
   inyecta el tema en `window.EDISAMP_SHOPIFY`; en local se leen del
   localStorage del admin. Ninguno de los dos es secreto (el preset unsigned es
   publico por diseno), pero tampoco tienen por que estar en el codigo.
   ========================================================================== */

const PANIER = (() => {
'use strict';

const CLE_CFG = 'edisamp.cloudinary';
const MAX_PROPERTY = 4000;          // margen sobre los ~4 Ko del spec

/* ------------------------------ configuracion ---------------------------- */
function config() {
  const inj = (typeof window !== 'undefined' && window.EDISAMP_SHOPIFY) || {};
  let local = {};
  try { local = JSON.parse(localStorage.getItem(CLE_CFG) || '{}'); } catch (e) {}
  return {
    cloudName: inj.cloudName || local.cloudName || '',
    preset:    inj.preset    || local.preset    || '',
    dossier:   inj.dossier   || local.dossier   || 'edisamp/personnalisations',
  };
}

function setConfig(c) {
  try { localStorage.setItem(CLE_CFG, JSON.stringify(c || {})); } catch (e) {}
}

const configure = () => { const c = config(); return !!(c.cloudName && c.preset); };

/* ¿Estamos DENTRO de la tienda? El add-to-cart real solo existe ahi. En local
   se simula y se ensena lo que se mandaria, que es lo que permite depurarlo
   sin tocar la tienda de verdad. */
const dansLaBoutique = () => /(^|\.)edisamp\.net$|myshopify\.com$/.test(location.hostname);

/* -------------------------------- Cloudinary ----------------------------- */
async function televerser(dataUrl, nom) {
  const c = config();
  if (!c.cloudName || !c.preset) {
    /* FUERA DE LA TIENDA, SIMULA. El add-to-cart ya se simulaba en local
       (ver ajouter()), pero la subida de fotos abortaba antes de llegar ahi:
       o sea que el recorrido completo —anadir varias piezas, ver el contador
       subir, encadenar la siguiente— solo se podia probar en la tienda de
       verdad, con clientes dentro. Eso es exactamente donde no se prueba.
       En la tienda el comportamiento no cambia: sin Cloudinary se para, que
       es lo correcto, porque ahi la foto SI tiene que viajar. */
    if (!dansLaBoutique()) {
      console.info('[EDISAMP] simulation Cloudinary (hors boutique)', nom || '');
      return 'simulation://' + encodeURIComponent(nom || 'image');
    }
    throw new Error('Cloudinary non configuré.');
  }
  const fd = new FormData();
  /* Cloudinary acepta el dataUrl tal cual como `file`: evita convertir a Blob
     y volver a leerlo, que en un movil es memoria de mas. */
  fd.append('file', dataUrl);
  fd.append('upload_preset', c.preset);
  if (c.dossier) fd.append('folder', c.dossier);
  if (nom) fd.append('context', 'nom=' + String(nom).slice(0, 60));

  const r = await fetch(`https://api.cloudinary.com/v1_1/${c.cloudName}/image/upload`,
                        { method:'POST', body: fd });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.secure_url) {
    throw new Error((j.error && j.error.message) || 'Échec de l’envoi de l’image.');
  }
  return j.secure_url;
}

/* -------------------------- aligerar el JSON ----------------------------- */
/* Sustituye TODOS los dataUrl por las URL subidas y devuelve el JSON listo
   para la property. Devuelve tambien la lista de originales, que va en su
   propia property para produccion. */
function alegerJSON(json, urls) {
  const copia = JSON.parse(JSON.stringify(json));
  const originales = [];
  let i = 0;
  (copia.vues || []).forEach(v => (v.zones || []).forEach(z => {
    (z.elements || []).forEach(e => {
      if (!e.source) return;
      const u = urls[i] || {};
      /* La miniatura NO se manda: para producir se usa el original, y para
         mirar el pedido esta el apercu. Mandar las dos seria duplicar peso
         dentro de un limite de 4 Ko. */
      delete e.source.dataUrl;
      if (e.source.dataUrlOriginale) {
        delete e.source.dataUrlOriginale;
        e.source.url = u.original || null;
        if (u.original) originales.push(u.original);
      }
      i++;
    });
  }));
  return { json: copia, originales };
}

/* Cuenta cuantos elementos con imagen hay, para saber que subir. */
function elementsAvecImage(json) {
  const out = [];
  (json.vues || []).forEach(v => (v.zones || []).forEach(z => {
    (z.elements || []).forEach(e => {
      if (e.source) out.push(e);
    });
  }));
  return out;
}

/* --------------------------- variante de Shopify ------------------------- */
/* Casa la talla y el acabado elegidos en el Studio con la variante real. Si no
   hay coincidencia se devuelve la primera DISPONIBLE y se avisa: mandar una
   variante equivocada en silencio seria vender una talla que el cliente no
   pidio. */
function choisirVariante(produit, taille, finition) {
  const vs = produit.variantes || [];
  if (!vs.length) return { id: produit.varianteId, exacte: false, motif:'sans variantes' };

  const veut = [taille, finition].filter(Boolean).map(x => String(x).toLowerCase());
  if (!veut.length) return { id: vs[0].id, exacte: true };

  const score = v => {
    const opts = (v.options || []).map(o => String(o).toLowerCase());
    return veut.filter(x => opts.some(o => o === x || o.includes(x))).length;
  };
  let mejor = null, max = -1;
  vs.forEach(v => { const s = score(v); if (s > max && v.disponible !== false) { max = s; mejor = v; } });

  if (!mejor) return { id: vs[0].id, exacte:false, motif:'aucune variante disponible' };
  return {
    id: mejor.id,
    exacte: max === veut.length,
    motif: max === veut.length ? null
         : `${max}/${veut.length} option(s) trouvée(s) — vérifiez les variantes du produit`,
  };
}

/* ------------------------------ properties ------------------------------- */
function construireProprietes(json, apercuUrl, originales, extra) {
  const p = json.produit || {};
  const zonas = (json.vues || []).flatMap(v => (v.zones || []).map(z => ({
    vue: v.nom || v.vue, zone: z.nom || z.zone,
    cm: z.cm, px: z.impressionPx,
    /* QUÉ hay en cada zona, no sólo que la hay. Franklin: « si el cliente
       puede escribir en 3 sitios, sitio 1, sitio 2, sitio 3, yo tengo que
       saber en qué sitio escribió para saber qué estilo quiere ». Con tres
       zonas de escritura, « Devant — Zone principale · Devant — Zone
       principale 2 » no dice nada: hay que abrir el JSON para saber qué texto
       va dónde, y eso en el taller no se hace. */
    /* `contenu` y `police` van en la RAÍZ del elemento, no dentro de un
       objeto `texte`. Lo comprobé volcando el JSON: leerlo mal dejaba la
       ficha en « Devant entier: — » con dos textos dentro. */
    textes: (z.elements || []).filter(e => e.type === 'texte' && e.contenu)
              .map(e => ({ contenu: e.contenu, police: e.police })),
    photos: (z.elements || []).filter(e => e.type === 'image').length,
    /* « No te olvides que hay un fondo »: si la zona lleva color, va en la
       ficha. Es material que se imprime y cambia lo que se cobra. */
    fond: z.fond || null,
  })));

  /* LO QUE LEE EL CLIENTE EN SU CARRITO: CORTO.
     Franklin, en el iPhone: « es demasiado largo, las descripciones… tendría
     que ser mucho más compacto ». Salía « Zone principale 4: « Franklin »
     (yellowtail) », « Aperçu: https://res.cloudinary.com/… » —un enlace de
     90 caracteres que además hacía que la página se moviera de lado—,
     « Taille: M » en un cojín y en un llavero, que no tienen tallas, y
     « Finition: Impression » donde no había nada que elegir.
     Visible queda sólo lo que el cliente decidió: sus textos y sus fotos, y
     la talla o el acabado SÓLO si el producto le dejaba elegirlos. Todo lo
     demás sigue llegando al pedido, pero con « _ » delante, que el carrito
     del tema no enseña. Nada se pierde para producción. */
  const conAlgo = zonas.filter(z => z.textes.length || z.photos || z.fond);
  const variasZonas = conAlgo.length > 1;
  const resume = conAlgo.map(z => {
    const qué = [];
    z.textes.forEach(t => qué.push(`« ${t.contenu} »`));
    if (z.photos) qué.push(z.photos === 1 ? '1 photo' : `${z.photos} photos`);
    if (z.fond) qué.push('fond de couleur');
    return (variasZonas ? `${z.zone} : ` : '') + qué.join(' + ');
  }).join(' · ') || 'Oui';
  /* El detalle largo —con la zona y la tipografía— es para el taller. */
  const detalle = zonas.map(z => {
    const qué = [];
    z.textes.forEach(t => qué.push(`« ${t.contenu} »` + (t.police ? ` (${t.police})` : '')));
    if (z.photos) qué.push(z.photos === 1 ? '1 photo' : `${z.photos} photos`);
    if (z.fond) qué.push(`fond ${z.fond}`);
    return `${z.zone}: ${qué.length ? qué.join(' + ') : '—'}`;
  }).join('  |  ');

  const x = extra || {};
  const props = {
    /* Visible para el cliente en el carrito. */
    'Personnalisation': resume,
    /* Ocultas (prefijo _) pero llegan al pedido: mecanismo estandar POD. */
    '_apercu': apercuUrl || '',
    '_detail': detalle,
    '_design_json': JSON.stringify(json),
    '_photo_originale': originales.join(' | '),
    '_zones': JSON.stringify(zonas),
    '_dpi_cible': String((json.impression && json.impression.dpi) || 300),
  };
  /* Para volver a abrir ESTE diseño desde el carrito (« Modifier »). */
  if (x.modifier) props['_modifier'] = x.modifier;
  if (p.taille)   props[x.tallaElegible ? 'Taille' : '_taille'] = p.taille;
  if (p.finition) props[x.finitionElegible ? 'Finition' : '_finition']
                    = p.finition === 'gravure' ? 'Gravure' : 'Impression';
  if (x.qualiteAcceptee) props['_qualite_acceptee'] = 'true';
  return props;
}

/* ============================================================================
   LA IDENTIDAD DE LA LÍNEA: QUÉ PRODUCTO, QUÉ VARIANTE, QUÉ PROYECTO
   ----------------------------------------------------------------------------
   Franklin: « cuando el cliente termina, el carrito debe recibir no solamente
   el producto y la cantidad; también la configuración: productId, variantId,
   projectId, personalizationId… No quiero que el cliente termine el
   personalizador y después Shopify añada simplemente una taza genérica sin
   relación con el proyecto ».

   El diseño entero ya viajaba (`_design_json`, `_zones`, `_apercu`, las fotos
   originales). Lo que faltaba eran los CUATRO IDENTIFICADORES que permiten
   atar esa línea a algo: dos los sabe la tienda (producto y variante) y dos
   los sabe el Studio (el proyecto guardado, y esta personalización concreta).

   `_personnalisation_id` se acuña aquí, en el momento de añadir: dos líneas
   del mismo proyecto —la misma taza pedida dos veces con un retoque entre
   medias— son dos personalizaciones distintas, y en el taller hay que poder
   distinguirlas sin comparar JSON.

   Todas con `_` delante: llegan al pedido y a la API, y el carrito no se las
   enseña al cliente, que no tiene nada que hacer con un número de 14 cifras.
   ========================================================================== */
function nouvelId(prefijo) {
  let cola = '';
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) cola = crypto.randomUUID().slice(0, 8);
  } catch (e) { /* navegador viejo: se cae al azar de abajo */ }
  if (!cola) cola = Math.random().toString(36).slice(2, 10);
  /* La fecha delante: así ordenan solos en una hoja de pedidos. */
  const d = new Date();
  const dia = d.toISOString().slice(0, 10).replace(/-/g, '');
  return `${prefijo}-${dia}-${cola}`;
}

function identite(x) {
  const d = x || {};
  const props = { '_personnalisation_id': d.personnalisationId || nouvelId('perso') };
  if (d.produitId    != null) props['_produit_id']    = String(d.produitId);
  if (d.handle)               props['_produit_handle'] = String(d.handle);
  if (d.varianteId   != null) props['_variante_id']   = String(d.varianteId);
  if (d.projetId)             props['_projet_id']     = String(d.projetId);
  return props;
}

/* El limite de 4 Ko es el que puede arruinar un pedido en silencio: si el JSON
   no cabe, es mejor mandarlo por URL que mandarlo truncado. */
async function garantirTaille(props) {
  const dj = props['_design_json'] || '';
  if (dj.length <= MAX_PROPERTY) return { props, parUrl:false, taille:dj.length };
  /* Plan B: el JSON entero se sube a Cloudinary como fichero y en la property
     va su URL. Asi el pedido nunca llega incompleto. */
  const url = await televerser(
    'data:application/json;base64,' + btoa(unescape(encodeURIComponent(dj))),
    'design-json');
  const copia = Object.assign({}, props);
  copia['_design_json'] = '';
  copia['_design_json_url'] = url;
  return { props: copia, parUrl:true, taille:dj.length };
}

/* ------------------------------- add to cart ----------------------------- */
async function ajouter(varianteId, proprietes, quantite) {
  if (!dansLaBoutique()) {
    console.info('[EDISAMP] simulation add-to-cart', { varianteId, proprietes });
    return { simule:true, varianteId, proprietes };
  }
  const r = await fetch('/cart/add.js', {
    method:'POST',
    headers:{ 'Content-Type':'application/json', 'Accept':'application/json' },
    body: JSON.stringify({
      items: [{ id: varianteId, quantity: quantite || 1, properties: proprietes }],
    }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.description || j.message || 'Ajout au panier refusé.');
  return j;
}

return { config, setConfig, configure, dansLaBoutique, televerser,
         alegerJSON, elementsAvecImage, choisirVariante,
         construireProprietes, garantirTaille, ajouter, identite, nouvelId,
         MAX_PROPERTY };
})();
